import { AutoLink } from '@betterment/js-runtime';
import { Box, H1, Icon, IconName, Link, Separator, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';

import useSidebarItems from './useSidebarItems';

type NavigationItem = {
  label: string;
  icon: string;
  httpMethod?: 'delete';
  destination: string;
  key: string;
};

interface SidebarItemProps extends NavigationItem {
  icon: IconName;
  isActive: boolean;
}

interface LogoButtonProps {
  largeFirmLogoPath: string | null | undefined;
  destinationPath: string;
  logoIsKnockedOut: boolean;
  firmName: string;
}
export interface SidebarNavProps {
  active: string;
  logoButton: LogoButtonProps;
  isAdmin: boolean;
  isCompliance: boolean;
  isFourOhOneKEnabled: boolean;
  isFourOhOneKOnly: boolean;
}

const sideBarItemColor = 'primitives.white.100';
const separatorColor = 'primitives.white.100alpha30';
const hoverStateColor = 'primitives.white.100alpha15';
const activeColor = 'primitives.white.100alpha30';

const LogoButton = ({ logoButton }: { logoButton: LogoButtonProps }) => {
  if (!logoButton.largeFirmLogoPath) {
    return (
      <Box
        css={{
          display: 'flex',
          alignItems: 'center',
          maxWidth: '200px',
          justifyContent: 'center',
          px: 'space.200',
          gap: 'space.100',
        }}
      >
        <Link href={logoButton.destinationPath} css={{ _hover: { textDecoration: 'none' } }}>
          <H1 variant="title100" css={{ color: 'primitives.white.100', fontWeight: '500' }}>
            {logoButton.firmName}
          </H1>
        </Link>
      </Box>
    );
  }

  return (
    <Link href={logoButton.destinationPath}>
      <Box
        css={{
          display: 'flex',
          minHeight: 'size.300',
          alignItems: 'center',
          maxWidth: '200px',
          px: 'space.200',
          bg: logoButton.logoIsKnockedOut ? 'transparent' : 'primitives.white.100',
        }}
      >
        <img src={logoButton.largeFirmLogoPath} alt="Advisor firm logo" />
      </Box>
    </Link>
  );
};

const SidebarItem = ({ icon, label, destination, httpMethod, isActive }: SidebarItemProps) => {
  const linkProps = httpMethod ? { rel: 'nofollow', 'data-method': httpMethod } : {};
  return (
    <AutoLink
      to={destination}
      className={css({
        borderRadius: 'medium',
        display: 'flex',
        alignItems: 'center',
        gap: 'space.150',
        textDecoration: 'none',
        cursor: 'pointer',
        py: 'space.100',
        px: 'space.150',
        mb: 'space.25',
        _hover: {
          backgroundColor: isActive ? activeColor : hoverStateColor,
          textDecoration: 'none',
        },
        backgroundColor: isActive ? activeColor : 'transparent',
      })}
      {...linkProps}
    >
      <Icon
        name={icon}
        size="small"
        css={{ alignSelf: 'center', gridRowEnd: 'span 2', borderRadius: 'medium' }}
        color="icon.onPrimary"
      />
      <Text variant="text50" css={{ overflow: 'hidden', textOverflow: 'ellipsis', color: sideBarItemColor }}>
        {label}
      </Text>
    </AutoLink>
  );
};

const SidebarNav = ({
  active,
  logoButton,
  isAdmin,
  isCompliance,
  isFourOhOneKEnabled,
  isFourOhOneKOnly,
}: SidebarNavProps) => {
  const { primaryNavItems, adminItems, accountUtilityItems } = useSidebarItems({
    isAdmin,
    isCompliance,
    isFourOhOneKEnabled,
    isFourOhOneKOnly,
  });
  return (
    <nav>
      <Box
        css={{
          height: '100vh',
          gap: 'space.300',
          backgroundColor: 'primitives.navy.100',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          pt: 'space.300',
          pb: 'space.200',
          px: 'space.100',
          width: '220px',
        }}
      >
        <LogoButton logoButton={logoButton} />

        <Box>
          {primaryNavItems.map(item => (
            <SidebarItem
              key={item.key}
              label={item.label}
              icon={item.icon as IconName}
              httpMethod={item.httpMethod}
              destination={item.destination}
              isActive={active === item.key}
            />
          ))}
          <Box css={{ px: 'space.150' }}>
            <Separator css={{ backgroundColor: separatorColor, my: 'space.150' }} />
          </Box>
          {adminItems.length > 0 && (
            <>
              {adminItems.map(item => (
                <SidebarItem
                  key={item.key}
                  label={item.label}
                  icon={item.icon as IconName}
                  httpMethod={item.httpMethod}
                  destination={item.destination}
                  isActive={active === item.key}
                />
              ))}
              <Box css={{ px: 'space.150' }}>
                <Separator css={{ backgroundColor: separatorColor, my: 'space.150' }} />
              </Box>
            </>
          )}
          {accountUtilityItems.map(item => (
            <SidebarItem
              key={item.key}
              label={item.label}
              icon={item.icon as IconName}
              httpMethod={item.httpMethod}
              destination={item.destination}
              isActive={active === item.key}
            />
          ))}
        </Box>
      </Box>
    </nav>
  );
};

export default SidebarNav;
